import styled, { css, keyframes } from "styled-components";

// ========================================
// LAYOUT
// ========================================

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  margin-top: 115px;
  width: 100%;
  height: calc(100vh - 115px);
  box-sizing: border-box;
  overflow: hidden;
`;

export const CalendarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

// ========================================
// HEADER
// ========================================

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 1000px) {
    flex-wrap: wrap;
    padding: 10px 16px;
    gap: 8px;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1000px) {
    flex: 1;
    min-width: 0;
  }
`;

export const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 1000px) {
    order: 3;
    width: 100%;
    justify-content: space-between;
    gap: 8px;
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1000px) {
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    gap: 6px;
  }
`;

export const NavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #5f6368;
  transition: background 0.2s;

  &:hover {
    background: #f1f3f4;
  }

  svg {
    font-size: 20px;
  }
`;

export const TodayButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #dadce0;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #3c4043;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: #f1f3f4;
  }
`;

export const HeaderTitle = styled.h2`
  font-size: 22px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  color: #3c4043;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

export const MonthDropdown = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #5f6368;

  &:hover {
    background: #f1f3f4;
  }
`;

export const ViewModeToggle = styled.div`
  display: flex;
  border: 1px solid #dadce0;
  border-radius: 4px;
  overflow: hidden;
`;

export const ViewModeButton = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: none;
  background: ${({ $active }) => ($active ? "#e8eaed" : "transparent")};
  cursor: pointer;
  font-size: 13px;
  font-family: Inter, sans-serif;
  color: #3c4043;
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  transition: background 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) => ($active ? "#e8eaed" : "#f1f3f4")};
  }

  & + & {
    border-left: 1px solid #dadce0;
  }

  @media (max-width: 760px) {
    padding: 6px 8px;
    font-size: 11px;

    span {
      display: none;
    }

    &::after {
      content: attr(data-short);
    }
  }
`;

export const NewEventButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border: none;
  background: #2d5bff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #fff;
  font-weight: 500;
  white-space: nowrap;
  transition: background 0.2s, box-shadow 0.2s;

  &:hover {
    background: #1a44e0;
    box-shadow: 0 2px 8px rgba(45, 91, 255, 0.3);
  }

  svg {
    font-size: 18px;
    flex-shrink: 0;
  }

  span {
    @media (max-width: 600px) {
      display: none;
    }
  }

  @media (max-width: 600px) {
    padding: 8px;
    gap: 0;
  }
`;

export const NewTaskButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border: 1px solid #2d5bff;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #2d5bff;
  font-weight: 500;
  white-space: nowrap;
  transition: background 0.2s, box-shadow 0.2s;

  &:hover {
    background: #f0f4ff;
    box-shadow: 0 2px 8px rgba(45, 91, 255, 0.15);
  }

  svg {
    font-size: 18px;
    flex-shrink: 0;
  }

  span {
    @media (max-width: 600px) {
      display: none;
    }
  }

  @media (max-width: 600px) {
    padding: 8px;
    gap: 0;
  }
`;

const spinAnim = keyframes`
  to { transform: rotate(360deg); }
`;

export const RefreshButton = styled.button<{ $spinning?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #dadce0;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #5f6368;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #f1f3f4;
    color: #2d5bff;
  }

  svg {
    font-size: 18px;
    ${({ $spinning }) =>
      $spinning &&
      css`
        animation: ${spinAnim} 0.7s linear infinite;
      `}
  }
`;

export const SyncToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 13px;
  font-family: Inter, sans-serif;
  color: #5f6368;
  white-space: nowrap;
  flex: 1;
  min-width: 0;

  svg {
    color: #2d5bff;
    font-size: 18px;
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 1200px) {
      display: none;
    }
  }

  @media (max-width: 1000px) {
    flex: unset;
    padding: 4px 8px;
  }
`;

// ========================================
// GRID CALENDÁRIO
// ========================================

export const CalendarGrid = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

export const DayHeaderRow = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: 60px repeat(${({ $columns }) => $columns}, 1fr);
  border-bottom: 1px solid #dadce0;
  background: #fff;
  flex-shrink: 0;
`;

export const TimeGutterHeader = styled.div`
  width: 60px;
  border-right: 1px solid #dadce0;
`;

export const DayHeaderCell = styled.div<{ $isToday?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 4px;
  border-right: 1px solid #f0f0f0;
  cursor: default;

  &:last-child {
    border-right: none;
  }
`;

export const DayName = styled.span<{ $isToday?: boolean }>`
  font-size: 11px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  color: ${({ $isToday }) => ($isToday ? "#2d5bff" : "#70757a")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const DayNumber = styled.span<{ $isToday?: boolean }>`
  font-size: 24px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  line-height: 1.2;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  ${({ $isToday }) =>
    $isToday
      ? css`
          background: #2d5bff;
          color: #fff;
          font-weight: 500;
        `
      : css`
          color: #3c4043;
        `}
`;

// ========================================
// TIME GRID
// ========================================

export const TimeGridContainer = styled.div`
  display: flex;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
`;

export const TimeGridInner = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: 60px repeat(${({ $columns }) => $columns}, 1fr);
  width: 100%;
  position: relative;
`;

export const TimeGutter = styled.div`
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  border-right: 1px solid #dadce0;
`;

export const TimeLabel = styled.div`
  height: 60px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding-right: 8px;
  font-size: 11px;
  font-family: Inter, sans-serif;
  color: #70757a;
  position: relative;
  top: -6px;
`;

export const DayColumnContainer = styled.div`
  position: relative;
  border-right: 1px solid #f0f0f0;
  min-height: ${24 * 60}px;

  &:last-child {
    border-right: none;
  }
`;

export const HourRow = styled.div`
  height: 60px;
  border-bottom: 1px solid #f0f0f0;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    border-bottom: 1px dashed #f5f5f5;
  }
`;

// ========================================
// INDICADOR DE HORA ATUAL
// ========================================

export const CurrentTimeLine = styled.div<{ $top: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({ $top }) => $top}px;
  z-index: 3;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

export const CurrentTimeDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2d5bff;
  flex-shrink: 0;
  margin-left: -5px;
`;

export const CurrentTimeLineBar = styled.div`
  flex: 1;
  height: 2px;
  background: #2d5bff;
`;

// ========================================
// EVENTO NO CALENDÁRIO
// ========================================

const getEventColor = (showAs?: string, isCancelled?: boolean) => {
  if (isCancelled) return { bg: "#f5f5f5", border: "#bdbdbd", text: "#9e9e9e" };

  switch (showAs) {
    case "busy":
      return { bg: "#e8eaf6", border: "#3f51b5", text: "#1a237e" };
    case "tentative":
      return { bg: "#fff3e0", border: "#ff9800", text: "#e65100" };
    case "free":
      return { bg: "#e8f5e9", border: "#4caf50", text: "#1b5e20" };
    case "oof":
      return { bg: "#fce4ec", border: "#e91e63", text: "#880e4f" };
    case "workingElsewhere":
      return { bg: "#e0f2f1", border: "#009688", text: "#004d40" };
    default:
      return { bg: "#e3ecff", border: "#2d5bff", text: "#1a3cb8" };
  }
};

export const EventBlock = styled.div<{
  $top: number;
  $height: number;
  $showAs?: string;
  $isCancelled?: boolean;
  $left?: number;
  $width?: number;
}>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => `calc(${$left ?? 0}% + 2px)`};
  right: ${({ $left, $width }) => ($width ? "auto" : "4px")};
  ${({ $width }) => $width && `width: calc(${$width}% - 6px);`}
  height: ${({ $height }) => Math.max($height - 2, 18)}px;
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  overflow: hidden;
  z-index: 1;
  transition: box-shadow 0.2s, transform 0.1s;

  ${({ $showAs, $isCancelled }) => {
    const colors = getEventColor($showAs, $isCancelled);
    return css`
      background: ${colors.bg};
      border-left: 3px solid ${colors.border};
      color: ${colors.text};
    `;
  }}

  ${({ $isCancelled }) =>
    $isCancelled &&
    css`
      text-decoration: line-through;
      opacity: 0.7;
    `}

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }
`;

export const EventTitle = styled.div`
  font-size: 12px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

export const EventTime = styled.div`
  font-size: 10px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ========================================
// MODAL DE EVENTO
// ========================================

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e0e0e0;
`;

export const ModalTitle = styled.h3`
  font-size: 18px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  color: #202124;
  margin: 0;
`;

export const ModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #5f6368;
  flex-shrink: 0;
  transition: background 0.2s, opacity 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  &:active {
    background: rgba(0, 0, 0, 0.1);
  }
`;

export const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px;
  border-top: 1px solid #e0e0e0;
`;

export const ModalButton = styled.button<{
  $variant?: "primary" | "secondary" | "danger";
}>`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  ${({ $variant }) => {
    switch ($variant) {
      case "primary":
        return css`
          background: #2d5bff;
          color: #fff;
          border: none;
          &:hover {
            background: #1a44e0;
          }
          &:disabled {
            background: #c5cae9;
            cursor: not-allowed;
          }
        `;
      case "danger":
        return css`
          background: #fff;
          color: #d32f2f;
          border: 1px solid #d32f2f;
          &:hover {
            background: #fef1f1;
          }
        `;
      default:
        return css`
          background: #fff;
          color: #5f6368;
          border: 1px solid #dadce0;
          &:hover {
            background: #f1f3f4;
          }
        `;
    }
  }}
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormLabel = styled.label`
  font-size: 13px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  color: #5f6368;
`;

export const FormInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #dadce0;
  border-radius: 6px;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #202124;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2d5bff;
  }
`;

export const FormTextarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #dadce0;
  border-radius: 6px;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #202124;
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2d5bff;
  }
`;

export const FormRow = styled.div`
  display: flex;
  gap: 12px;

  > * {
    flex: 1;
  }
`;

export const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #3c4043;
  cursor: pointer;
  user-select: none;
`;

export const CustomCheckbox = styled.span<{ $checked?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid ${({ $checked }) => ($checked ? "#5C6BC0" : "#bdbdbd")};
  background: ${({ $checked }) => ($checked ? "#5C6BC0" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: "";
    width: 5px;
    height: 9px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translate(-1px, -1px);
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
    transition: opacity 0.15s ease;
  }

  &:hover {
    border-color: ${({ $checked }) => ($checked ? "#3F51B5" : "#9e9e9e")};
  }
`;

export const ToggleSwitch = styled.span<{ $checked?: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${({ $checked }) => ($checked ? "#5C6BC0" : "#dadce0")};
  position: relative;
  transition: background 0.25s ease;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $checked }) => ($checked ? "18px" : "2px")};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: left 0.25s ease;
  }
`;

// ========================================
// EVENT DETAIL POPUP
// ========================================

export const EventDetailPopup = styled.div`
  position: fixed;
  z-index: 1400;
  background: #fff;
  border-radius: 12px;
  width: 360px;
  max-height: calc(100vh - 16px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: popupFadeIn 0.15s ease-out;

  @keyframes popupFadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const EventDetailHeader = styled.div<{ $color?: string }>`
  padding: 16px 20px;
  background: ${({ $color }) => $color || "#2d5bff"};
  color: #fff;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

export const EventDetailCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:active {
    background: rgba(255, 255, 255, 0.4);
  }
`;

export const EventDetailTitle = styled.h4`
  font-size: 16px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  margin: 0;
  color: #fff;
  word-break: break-word;
`;

export const EventDetailBody = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

export const EventDetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  font-family: Inter, sans-serif;
  color: #5f6368;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #70757a;
  }
`;

export const EventDetailFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 16px 12px;
`;

export const EventDetailLink = styled.a`
  font-size: 13px;
  font-family: Inter, sans-serif;
  color: #2d5bff;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

export const EventDetailActionButton = styled.button<{
  $color?: string;
}>`
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $color }) => $color || "#dadce0"};
  background: transparent;
  color: ${({ $color }) => $color || "#5f6368"};
  transition: background 0.2s;

  &:hover {
    background: ${({ $color }) => ($color ? `${$color}10` : "#f1f3f4")};
  }
`;

// ========================================
// LOADING / EMPTY STATE
// ========================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: #5f6368;
  font-family: Inter, sans-serif;
`;

export const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #e0e0e0;
  border-top-color: #2d5bff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: #d32f2f;
  font-family: Inter, sans-serif;
  text-align: center;
  padding: 24px;
`;

export const RetryButton = styled.button`
  padding: 8px 20px;
  border: 1px solid #2d5bff;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #2d5bff;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: #f0f4ff;
  }
`;

// ========================================
// ALL DAY EVENTS
// ========================================

export const AllDayRow = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: 60px repeat(${({ $columns }) => $columns}, 1fr);
  border-bottom: 1px solid #dadce0;
  background: #fff;
  min-height: 0;
  flex-shrink: 0;
`;

export const AllDayGutter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  font-size: 11px;
  font-family: Inter, sans-serif;
  color: #70757a;
  border-right: 1px solid #dadce0;
`;

export const AllDayCell = styled.div`
  padding: 2px 4px;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 28px;

  &:last-child {
    border-right: none;
  }
`;

export const AllDayEvent = styled.div<{
  $showAs?: string;
  $isCancelled?: boolean;
}>`
  font-size: 11px;
  font-family: Inter, sans-serif;
  padding: 2px 6px;
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $showAs, $isCancelled }) => {
    const colors = getEventColor($showAs, $isCancelled);
    return css`
      background: ${colors.bg};
      border-left: 2px solid ${colors.border};
      color: ${colors.text};
    `;
  }}

  ${({ $isCancelled }) =>
    $isCancelled &&
    css`
      text-decoration: line-through;
      opacity: 0.7;
    `}

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  }
`;

// ========================================
// MINI CALENDAR (date picker)
// ========================================

export const MiniCalendarOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
`;

export const MiniCalendarContainer = styled.div`
  position: absolute;
  top: 126px;
  left: 48%;
  transform: translateX(-50%);
  z-index: 100;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
  padding: 16px;
  width: 300px;
  font-family: Inter, sans-serif;
`;

export const MiniCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const MiniCalendarTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #202124;
`;

export const MiniCalendarNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #5f6368;
  transition: background 0.15s;

  &:hover {
    background: #f1f3f4;
  }

  svg {
    font-size: 18px;
  }
`;

export const MiniCalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
`;

export const MiniCalendarWeekday = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #70757a;
  text-align: center;
  padding: 4px 0 8px;
  text-transform: uppercase;
`;

export const MiniCalendarDay = styled.button<{
  $isToday?: boolean;
  $isSelected?: boolean;
  $isCurrentMonth?: boolean;
}>`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 13px;
  font-family: Inter, sans-serif;
  font-weight: ${({ $isToday }) => ($isToday ? "600" : "400")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1px auto;
  transition: background 0.15s, color 0.15s;

  ${({ $isSelected, $isToday, $isCurrentMonth }) => {
    if ($isSelected) {
      return css`
        background: #2d5bff;
        color: #fff;
      `;
    }
    if ($isToday) {
      return css`
        background: transparent;
        color: #2d5bff;
        border: 2px solid #2d5bff;
      `;
    }
    return css`
      background: transparent;
      color: ${$isCurrentMonth ? "#202124" : "#bdbdbd"};
    `;
  }}

  &:hover {
    ${({ $isSelected }) =>
      !$isSelected &&
      css`
        background: #f0f4ff;
      `}
  }
`;

export const MiniCalendarTodayButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  color: #2d5bff;
  transition: background 0.15s;

  &:hover {
    background: #f0f4ff;
  }
`;
